/**
 * Bundle 138 P0-3 - Public pricing page (events platform positioning).
 *
 * REVISED 2026-02-18 per user note: Dostination is an events platform,
 * not a dating product. Tickets follow a three-tier release model
 * (early bird / mid tier / final) that is set per event by the host.
 * This page intentionally does NOT publish specific dollar amounts;
 * the current tier price is only visible on the event's own page at
 * time of purchase.
 *
 * Route: /pricing. Public, no auth, indexable by search engines
 * (Stripe compliance).
 *
 * Sections:
 *   1) How Dostination pricing works (hero)
 *   2) Three-tier release model explanation (no dollar amounts)
 *   3) Live events (cards linking to each event's public page for
 *      the current tier price)
 *   4) Fees and refunds
 *   5) Sample checkout flow
 *   6) Legal footer links
 *
 * Guardrails: no em/en dashes; no eslint-disable; no "photo"/"image"
 * in HTML alt.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Sparkles, MapPin, Wallet, ArrowRight, Layers, ShieldCheck } from 'lucide-react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) {
    return '';
  }
}

const TIERS = [
  {
    key: 'early_bird',
    label: 'Early bird',
    body: 'Released first at the lowest price the host has set for that event. Limited quantity. Sold in order of RSVP.',
  },
  {
    key: 'mid',
    label: 'Mid tier',
    body: 'Released once early bird sells out or after a scheduled date set by the host. Priced above early bird.',
  },
  {
    key: 'final',
    label: 'Final release',
    body: 'Released closest to the event date. Highest tier the host has set. Fewest seats. Priced above mid tier.',
  },
];

const PricingPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let mounted = true;
    api.get('/pricing/summary')
      .then((r) => { if (mounted) setSummary(r.data); })
      .catch((e) => { if (mounted) setErr(e?.message || 'Failed to load pricing.'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      {/* Hero */}
      <section className="border-b border-neutral-100 bg-gradient-to-br from-neutral-50 via-white to-secondary-50/50">
        <div className="container mx-auto px-4 md:px-8 py-14 max-w-5xl">
          <div className="flex items-center gap-3 text-secondary-800 text-xs uppercase tracking-widest font-medium mb-3">
            <Sparkles className="h-4 w-4" /> Transparent, event-first pricing
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-900 leading-tight">
            How Dostination pricing works
          </h1>
          <p
            className="text-primary-700 mt-4 max-w-3xl text-base sm:text-lg leading-relaxed"
            data-testid="pricing-positioning-copy"
          >
            Dostination is an events platform for intentional, in-person connection experiences. This is
            not a dating or matchmaking product. There is no monthly membership fee. You pay once, per
            event, for the ticket tier you choose at time of purchase. Prices are set per event by the
            host and are always shown live on that event page before you hand off to Stripe.
          </p>
        </div>
      </section>

      {/* Section 1: Event tickets summary */}
      <section className="container mx-auto px-4 md:px-8 py-14 max-w-5xl">
        <div className="grid gap-8 md:grid-cols-[auto_1fr] md:items-start">
          <div className="text-secondary-700"><Ticket className="h-8 w-8" /></div>
          <div>
            <h2 className="font-display text-xl font-semibold text-primary-900 mb-2">1. Event tickets</h2>
            <p className="text-primary-700 leading-relaxed">
              Each Dostination event has its own ticket pricing set independently by the host. Ticket
              releases follow a three-tier structure (early bird, mid tier, final release). The exact
              price at each tier is defined at event creation and displayed on the public event page.
            </p>
            <p className="text-neutral-600 text-sm mt-3" data-testid="pricing-no-dollar-disclosure">
              We deliberately do not publish specific dollar amounts on this page because pricing
              evolves per event and per tier. You always see the exact price of the tier you are
              about to purchase before checkout.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: Three-tier release model */}
      <section className="bg-neutral-50 border-y border-neutral-100">
        <div className="container mx-auto px-4 md:px-8 py-14 max-w-5xl">
          <div className="grid gap-8 md:grid-cols-[auto_1fr] md:items-start">
            <div className="text-secondary-700"><Layers className="h-8 w-8" /></div>
            <div className="w-full">
              <h2 className="font-display text-xl font-semibold text-primary-900 mb-2">
                2. The three-tier release model
              </h2>
              <p className="text-primary-700 leading-relaxed mb-6">
                Every event follows the same release cadence. Prices rise from one tier to the next.
                Users always see the exact price of the current tier before payment; there are no
                surprise fees added later in checkout.
              </p>
              <ol className="grid gap-4 md:grid-cols-3" data-testid="pricing-tier-model">
                {TIERS.map((t, idx) => (
                  <li
                    key={t.key}
                    data-testid={`pricing-tier-${t.key}`}
                    className="rounded-xl border border-neutral-200 bg-white p-5 shadow-soft"
                  >
                    <div className="text-[11px] uppercase tracking-widest text-neutral-500">Tier {idx + 1}</div>
                    <div className="font-display text-lg font-semibold text-primary-900 mt-1">
                      {t.label}
                    </div>
                    <p className="text-sm text-primary-700 mt-2 leading-relaxed">{t.body}</p>
                  </li>
                ))}
              </ol>
              <div className="mt-6 flex items-center gap-3 text-sm text-primary-700 bg-white border border-neutral-200 rounded-lg p-4">
                <ShieldCheck className="h-5 w-5 text-secondary-700 shrink-0" />
                <span>
                  Price transparency: the current tier price is fetched live from the event page at
                  checkout. If a tier sells out while you are browsing, the next tier price is shown
                  before Stripe payment begins.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Live events (no dollar amounts) */}
      <section className="container mx-auto px-4 md:px-8 py-14 max-w-5xl">
        <div className="grid gap-8 md:grid-cols-[auto_1fr] md:items-start">
          <div className="text-secondary-700"><MapPin className="h-8 w-8" /></div>
          <div className="w-full">
            <h2 className="font-display text-xl font-semibold text-primary-900 mb-2">3. Currently open events</h2>
            <p className="text-primary-700 leading-relaxed mb-6">
              Below is the list of events currently accepting tickets. To see the current tier price for
              any event, open its page. Prices are set per event and update as tiers sell through.
            </p>

            {loading ? (
              <div className="text-neutral-500 text-sm">Loading events...</div>
            ) : err ? (
              <div className="text-accent-700 text-sm">{err}</div>
            ) : !summary?.has_purchasable ? (
              <div
                data-testid="pricing-page-live-events-empty"
                className="rounded-lg border border-neutral-200 bg-white p-5 text-sm text-primary-800"
              >
                No public events are currently open for tickets. Browse
                <Link to="/events" className="text-secondary-700 underline mx-1">upcoming events</Link>
                or check back soon.
              </div>
            ) : (
              <ul
                className="grid gap-4 sm:grid-cols-2"
                data-testid="pricing-page-live-events"
              >
                {summary.live_events.map((ev) => (
                  <li
                    key={ev.id}
                    data-testid={`pricing-live-event-${ev.slug || ev.id}`}
                    className="rounded-xl border border-neutral-200 bg-white p-5 shadow-soft hover:shadow-md transition-shadow flex flex-col"
                  >
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-500">
                      <span>{ev.city || 'TBD'}</span>
                      {ev.start_datetime ? <span>* {formatDate(ev.start_datetime)}</span> : null}
                    </div>
                    <div className="font-display text-lg font-semibold text-primary-900 mt-1">
                      {ev.title}
                    </div>
                    <p className="text-xs text-neutral-500 mt-2">
                      Current tier price is displayed on the event page.
                    </p>
                    <div className="mt-4">
                      <Link
                        to={ev.event_url || (ev.slug ? `/events/${ev.slug}` : '/events')}
                        data-testid={`pricing-live-event-cta-${ev.slug || ev.id}`}
                        className="inline-flex items-center gap-1 text-sm text-secondary-700 hover:text-secondary-800 font-medium"
                      >
                        View pricing on event page <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* Section 4: Fees & refunds */}
      <section className="bg-neutral-50 border-y border-neutral-100">
        <div className="container mx-auto px-4 md:px-8 py-14 max-w-5xl">
          <div className="grid gap-8 md:grid-cols-[auto_1fr] md:items-start">
            <div className="text-secondary-700"><Wallet className="h-8 w-8" /></div>
            <div>
              <h2 className="font-display text-xl font-semibold text-primary-900 mb-2">4. Fees and refunds</h2>
              <ul className="text-primary-700 leading-relaxed space-y-3 list-disc pl-5">
                <li>
                  <span className="font-medium">Platform fees.</span> The price you see on the event page
                  is the price you pay. Dostination absorbs card processing on most tiers; when a host
                  chooses to pass a fee through, it is disclosed on the event page and inside the
                  Stripe checkout summary.
                </li>
                <li>
                  <span className="font-medium">Refund policy.</span> Except where required by law or
                  explicitly stated on the event page, tickets are non-refundable once processed. If
                  Dostination cancels an event we offer a full refund or a transfer to another event,
                  at your option. See the
                  <Link to="/terms#payments" className="text-secondary-700 underline ml-1">
                    Terms section 6
                  </Link> for the full policy.
                </li>
                <li>
                  <span className="font-medium">Transfers.</span> Some events allow transfer of your
                  ticket to another approved guest before check-in. Look for a Transfer button on your
                  ticket page or email
                  <a
                    href="mailto:support@dostination.com"
                    className="text-secondary-700 underline ml-1"
                  >
                    support@dostination.com
                  </a>.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Sample flow */}
      <section className="container mx-auto px-4 md:px-8 py-14 max-w-5xl">
        <h2 className="font-display text-xl font-semibold text-primary-900 mb-4">5. Sample checkout flow</h2>
        <ol className="text-primary-700 space-y-3 list-decimal pl-6">
          <li>Pick an event on the <Link to="/events" className="text-secondary-700 underline">Events page</Link>.</li>
          <li>Read the event details. The event page displays the current active tier (early bird, mid, or final) and its exact price.</li>
          <li>Click the tier and hand off to Stripe. Stripe collects your card on their PCI-compliant page. You are charged the exact price shown on the event page.</li>
          <li>On success you are redirected back to Dostination with a confirmation and a QR ticket you can present at check-in.</li>
        </ol>
        <div className="mt-6">
          <Link to="/events">
            <Button className="bg-primary-800 hover:bg-primary-900 text-white" data-testid="pricing-cta-events">
              Browse events <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer legal links */}
      <section className="container mx-auto px-4 md:px-8 py-10 max-w-5xl text-sm text-neutral-600">
        <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center">
          <Link to="/terms" className="hover:text-primary-900" data-testid="pricing-page-terms-link">Terms</Link>
          <span aria-hidden="true">*</span>
          <Link to="/acceptable-use" className="hover:text-primary-900" data-testid="pricing-page-aup-link">Acceptable Use</Link>
          <span aria-hidden="true">*</span>
          <Link to="/privacy" className="hover:text-primary-900" data-testid="pricing-page-privacy-link">Privacy</Link>
          <span aria-hidden="true">*</span>
          <a href="mailto:support@dostination.com" className="hover:text-primary-900">Contact</a>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
